import React, {useEffect, useRef} from 'react'
import PropTypes from 'prop-types'
import {Box, useMultiStyleConfig} from '@chakra-ui/react'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {useGoogleTagManager} from '../../../custom-api/hooks/use-google-tag-manager'
import {basic_gtm_event} from '../../../custom-api/layers/default'
import {reinitFeefo} from '../../../custom-api/hooks/use-feefo'
import ProductTile from '../../product-tile'

const SearchModalProducts = ({product, index, onCloseModal, searchResults}) => {
    const navigate = useNavigation()
    const {addLayer} = useGoogleTagManager()
    const searchStyles = useMultiStyleConfig('Search', {})
    const tileRef = useRef(null)
    const discountPercentage = Number(product?.c_discountPercentage)
    const hasValidDiscount =
        discountPercentage > 0 && discountPercentage < 100 && Number(product?.price) > 0
    const derivedListPrice = hasValidDiscount
        ? Number((Number(product.price) / (1 - discountPercentage / 100)).toFixed(2))
        : undefined
    const normalizedProduct = {
        ...product?.representedProduct,
        ...product,
        productId:
            product?.c_subModelIdentifier ||
            product?.productId ||
            product?.id ||
            product?.representedProduct?.productId,
        productPromotions:
            product?.productPromotions || product?.representedProduct?.productPromotions,
        tieredPrices:
            product?.tieredPrices?.length || product?.representedProduct?.tieredPrices?.length
                ? product?.tieredPrices || product?.representedProduct?.tieredPrices
                : derivedListPrice
                ? [{quantity: 1, price: derivedListPrice}]
                : undefined,
        c_badgeCommerce1:
            product?.c_badgeCommerce1 || product?.representedProduct?.c_badgeCommerce1,
        c_badgeCommerce2:
            product?.c_badgeCommerce2 || product?.representedProduct?.c_badgeCommerce2,
        c_PLPkeyFeature1:
            product?.c_PLPkeyFeature1 || product?.representedProduct?.c_PLPkeyFeature1,
        c_PLPkeyFeature2:
            product?.c_PLPkeyFeature2 || product?.representedProduct?.c_PLPkeyFeature2,
        c_PLPkeyFeature3:
            product?.c_PLPkeyFeature3 || product?.representedProduct?.c_PLPkeyFeature3,
    }
    const productForTile = {
        ...normalizedProduct,
        representedProduct: {
            ...normalizedProduct,
            ...product?.representedProduct,
        },
    }

    useEffect(() => {
        const refreshReviews = () => {
            reinitFeefo()

            const reviewElement = tileRef.current?.querySelector('[data-bv-product-id]')
            if (!reviewElement || !window.BV?.ui) return

            window.BV.ui('rr', 'inline_ratings', {
                productId: reviewElement.getAttribute('data-bv-product-id'),
                container: reviewElement,
            })
        }

        const timeoutId = window.setTimeout(refreshReviews, 500)
        return () => window.clearTimeout(timeoutId)
    }, [productForTile.productId])

    const buildProductNavigationHref = (href, pid, fallbackProductId) => {
        const baseHref =
            href || (fallbackProductId ? `/p/${encodeURIComponent(fallbackProductId)}.html` : '')

        if (!baseHref || !pid || /([?&])pid=/.test(baseHref)) {
            return baseHref
        }

        return `${baseHref}${baseHref.includes('?') ? '&' : '?'}pid=${encodeURIComponent(pid)}`
    }

    const handleProductLayerClick = async () => {
        const cleanSearchText = (searchResults?.query || '').replace(/\*+$/, '') // Remove any trailing asterisks
        localStorage.setItem('fromISP', true)
        await addLayer(
            basic_gtm_event.bind(this, {
                event: 'internalSearch',
                eventCategory: 'engagement',
                eventAction: 'internalSearch-productClick',
                eventLabel: cleanSearchText,
                resultsAmount: searchResults?.total || 0,
            }),
        )
        onCloseModal()
    }

    const handleProductNavigation = async (href = '') => {
        const pid = productForTile?.representedProduct?.id || productForTile?.productId
        const productNavigationHref = buildProductNavigationHref(
            href,
            pid,
            productForTile?.productId,
        )

        await handleProductLayerClick()
        navigate(productNavigationHref)
    }

    const handleClickCapture = async (event) => {
        const clickableNode = event.target?.closest?.('a, button')

        if (clickableNode?.tagName?.toLowerCase() === 'button') {
            event.preventDefault()
            event.stopPropagation()
            await handleProductNavigation()
            return
        }

        const href = clickableNode?.getAttribute('href') || ''

        if (clickableNode) {
            event.preventDefault()
            event.stopPropagation()
        }

        await handleProductNavigation(href)
    }

    const handleKeyDown = (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return

        event.preventDefault()
        handleProductNavigation()
    }

    return (
        <Box
            ref={tileRef}
            data-testid={`search-product-tile-${index}`}
            onClickCapture={handleClickCapture}
            onKeyDown={handleKeyDown}
            role="link"
            tabIndex={0}
            sx={{
                '> div:hover, > div:focus-within': {
                    background: 'background.neutral.hover',
                },
                '> div:hover [data-testid="product-tile"], > div:focus-within [data-testid="product-tile"]':
                    {
                        background: 'background.neutral.hover',
                    },
                '& .chakra-image': {width: '100% !important', height: '100% !important'},
            }}
            {...searchStyles.productSearchTileWrapper}
        >
            <ProductTile
                product={productForTile}
                productIndex={index}
                isFromProductList={true}
                shouldHideReviews={false}
                shouldHideCompare={true}
                defaultBackground="background.base.over"
                imageStyle={{width: '100%', height: '100%'}}
            />
        </Box>
    )
}

SearchModalProducts.propTypes = {
    product: PropTypes.object,
    index: PropTypes.number,
    onCloseModal: PropTypes.func,
    searchResults: PropTypes.object,
}

export default SearchModalProducts
